/* eslint-disable no-useless-escape */
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  forwardRef,
  useCallback,
  useMemo
} from "react";
import ReactDOM from "react-dom";

import propTypes from "prop-types";
import classnames from "classnames";

import { useField } from "../form/Form";

import { createDataAttributes, isObject } from "../../utilities";
import { usePrevious, useResizeObserver } from "../../utilities/hooks";
import ErrorsForField from "../form/ErrorsForField";
import Label from "./Label";
import "./Select.scss";
import Icon from "../icons/Icon";

export const SelectMenu = forwardRef(function (
  {
    id,
    options,
    setShow,
    value,
    mainRef,
    onSelect,
    optionFilter,
    setOptionFilter,
    multi,
    showSearch
  },
  menuRef
) {
  const [pos, setPos] = useState({});
  const setSizeAndPosition = useCallback(
    element => {
      if (!element || !menuRef?.current) return;

      const mainRect = element.getBoundingClientRect();
      const menuRect = menuRef?.current
        ? menuRef.current.getBoundingClientRect()
        : null;

      if (!menuRect || !mainRect) return;

      const { height, width, top, bottom, left, right } = mainRect;
      const { height: menuHeight, width: menuWidth } = menuRect;
      const { innerHeight, innerWidth, scrollY } = window;

      const spaceAbove = top || 0;
      const spaceRight = innerWidth - (left || 0);
      const spaceBelow = innerHeight - (bottom || 0);
      const spaceLeft = right || 0;

      const positionStyles = {
        minWidth: width,
        left,
        top: top + scrollY + (height + 5)
      };

      // if not enough room bellow, show menu above - but only if there's space above
      if (spaceBelow < menuHeight && spaceAbove > menuHeight)
        positionStyles.top = top + scrollY - menuHeight - 5;

      // if not enough room right on menus that expand past the component, align menu to right edge
      if (spaceRight < menuWidth && spaceLeft > menuWidth)
        positionStyles.left = left - (menuWidth - width);

      setPos(positionStyles);
    },
    [menuRef]
  );

  /**
   * the mainRect can resize if using 'mulit'... this helps reposition the option menu after the render
   */
  useResizeObserver({
    ref: mainRef,
    onResize: setSizeAndPosition
  });

  // handle clicking outside of the menu to close the menu
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        mainRef.current &&
        !mainRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      )
        setShow(false);
    };
    const escKey = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShow(false);
      }
    };
    document.addEventListener("keydown", escKey, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("keydown", escKey, true);
    };
  }, [mainRef, menuRef, setShow]);

  /**
   * scroll the select menu if the selected option isn't visible in the menu
   */
  useLayoutEffect(() => {
    const menuRect = menuRef?.current
      ? menuRef.current.getBoundingClientRect()
      : null;
    const firstValue = value && Array.isArray(value) ? value[0] : value;
    const selectedOption =
      firstValue && menuRef?.current
        ? menuRef?.current.querySelector(`button[data-value="${firstValue}"]`)
        : null;
    const selectedOptionRect = selectedOption
      ? selectedOption.getBoundingClientRect()
      : null;

    if (menuRect && selectedOptionRect) {
      const scrollto = selectedOptionRect.top - menuRect.top;
      // only scroll if the option isn't visible
      if (scrollto > menuRect.height) menuRef.current.scrollTop = scrollto - 36;
    }

    /**
     * don't include 'value' because we don't want the div to scroll after the user changes the value
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuRef]);

  // allow select when navigating with keys
  const upDown = (e, i, o) => {
    if (e.key === "Tab") {
      setShow(false);
      return;
    }

    e.preventDefault();
    if (e.key === "Enter" || e.key === "Return") {
      onSelect(o);
      if (!multi) setShow(false);
    }
    // arrow up and is the first one
    if (e.key === "ArrowUp" && i === 0) {
      mainRef.current.querySelector("button.uikit__select__btn")?.focus();
      setShow(false);
    }
    // arrow up and not the first one
    else if (e.key === "ArrowUp" && i > 0)
      menuRef.current.querySelectorAll("button")[i - 1]?.focus();
    // arrow down and not the last item
    else if (e.key === "ArrowDown" && i !== options.length - 1)
      menuRef.current.querySelectorAll("button")[i + 1]?.focus();
    else if (e.key === "Escape") {
      setShow(false);
      mainRef.current.querySelector("button.uikit__select__btn")?.focus();
    } else if (e.key === "Home")
      menuRef.current.querySelectorAll("button")[0]?.focus();
    else if (e.key === "End")
      menuRef.current.querySelectorAll("button")[options.length]?.focus();
    else if (e.key.length === 1) {
      setOptionFilter(e.key);
      const searchInput = menuRef?.current?.querySelector("input");
      if (searchInput) searchInput?.focus();
    }
  };

  const handleLeaveSearchInput = e => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      mainRef.current.querySelector("button.uikit__select__btn")?.focus();
      setShow(false);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      menuRef.current.querySelectorAll("button")[0]?.focus();
    }
    if (e.key === "Tab") {
      setShow(false);
    }
    if (e.key === "Enter") {
      e.stopPropagation();
      e.preventDefault();
      if (filteredOptions.length > 0) onSelect(filteredOptions[0]);
    }
  };

  const getNodeText = lbl => {
    if (["string", "number"].includes(typeof lbl)) return lbl;
    if (lbl instanceof Array) return lbl.map(getNodeText).join("");
    if (typeof lbl === "object" && lbl) {
      return getNodeText(lbl.props.children);
    }
    return "";
  };

  const filteredOptions = options.filter(o => {
    if (!optionFilter) return true;
    // filter if search text exists
    const lbl = o?.label || "";
    const compareText = getNodeText(lbl).toLowerCase();
    return compareText.includes(optionFilter.toLowerCase());
  });

  const GroupHeader = ({ o, i }) =>
    o?.groupHeader &&
    o?.groupHeader !== filteredOptions?.[i - 1]?.groupHeader ? (
      <div className="uikit__select__menu__options-groupheader">
        {o.groupHeader}
      </div>
    ) : null;

  /**
   * This useEffect:
   * - helps make sure the input maintains the focus
   * - updates the size and position of the menu so it doesn't look so detatched from the input
   */
  useLayoutEffect(() => {
    const searchInput = menuRef?.current?.querySelector("input");
    if (searchInput || optionFilter) {
      searchInput?.focus();
      if (mainRef.current && menuRef.current)
        setSizeAndPosition(mainRef.current);
    }
  }, [mainRef, menuRef, optionFilter, setSizeAndPosition]);

  // detecting if search has focus... so that we don't hide it if the user deletes all of the text in the search
  const searchInputHasFocus =
    menuRef?.current?.querySelector("input") === document.activeElement;

  return ReactDOM.createPortal(
    <div
      id={`uikit__select__menu__${id}`}
      aria-labelledby={id}
      ref={menuRef}
      className={classnames("uikit__select__menu", {
        uikit__select__menu__show: !!menuRef.current,
        uikit__select__menu__isFiltered: !!optionFilter
      })}
      style={pos}
    >
      {(showSearch || !!optionFilter || searchInputHasFocus) &&
        options?.length > 0 && (
          <div className={classnames("uikit__select__menu__options-search")}>
            <input
              value={optionFilter}
              onChange={event => setOptionFilter(event.currentTarget.value)}
              onKeyDown={e => handleLeaveSearchInput(e)}
              id={`uikit__select__${id}__option-search`}
              type="text"
            />
            <Icon
              className="uikit__select__menu__options-search__icon"
              icon="farSearch"
            />
          </div>
        )}
      {!!optionFilter && filteredOptions?.length === 0 && (
        <div className="uikit__select__menu__options-search__no-results">
          <span>No results</span>
        </div>
      )}

      {filteredOptions.map((o, i) => {
        const isSelected = !multi ? value === o.value : value.includes(o.value);
        return (
          <React.Fragment key={o.key}>
            <GroupHeader o={o} i={i} />
            <button
              role="option"
              aria-selected={value === o.value}
              tabIndex="-1"
              // allow options to pass in a custom classname in the object data
              // make sure the css is clean and doesn't include invalid css characters
              className={classnames("uikit__select__menu__item", {
                [`${(o?.optionClassName || "").replace(
                  /\s|~|@|#|$|%|^\&|\*|\(|\)|{|}|\||\\|\[|\]|\?|\/|<|>|\"|\'|\+/g,
                  "_"
                )}`]: o?.optionClassName,
                uikit__select__menu__selected: isSelected
              })}
              onClick={event => {
                event.stopPropagation();
                event.preventDefault();
                onSelect(o);
              }}
              onKeyDown={e => {
                upDown(e, i, o);
              }}
              data-value={o.value}
              data-label={o.label}
            >
              {multi && (
                <Icon
                  className="uikit__select-multi__icon"
                  icon={isSelected ? "farCheck" : "fapSquare"}
                />
              )}
              {o.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>,
    document.body
  );
});

const Select = ({
  className,
  dataAttrib,
  disabled,
  id,
  size,
  name,
  label,
  labelElement,
  value,
  placeholder,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  options,
  required,
  hideLabel,
  noLabel,
  fieldErrors,
  isClearable,
  showSearch,
  hideErrors,
  requiredError,
  dataWrapperAttrib,
  isLoading,
  loadingMessage,
  multi,
  ...rest
}) => {
  const mainRef = useRef(null);
  const menuRef = useRef(null);

  const [show, setShow] = useState(false);
  const [selectFocused, setSelectFocused] = useState();
  const [selectedOption, setSelectedOption] = useState();
  const [optionFilter, setOptionFilter] = useState("");

  const makeOptionKey = useCallback(
    ({ i, key, label, value }) => {
      const k = (i || key || label || "").toString().replace(/[W]/g, "_");
      const v = (value || "").toString().replace(/[W]/g, "_");
      return `${id}_${k}_${v}`;
    },
    [id]
  );

  /**
   * this allows a Select to accept options that are both
   * an array of objects [{value, label}]
   * or an array of values [value1, value2]
   */
  const opts = useMemo(
    () =>
      options.length
        ? isObject(options[0])
          ? options.map(o => ({
              ...o,
              key: makeOptionKey({
                label: (o.label || "").toString(),
                value: o.value,
                k: o.key,
                i: o.id
              })
            }))
          : options.map(o => ({
              label: (o || "").toString(),
              value: o,
              key: makeOptionKey({
                label: (o || "").toString(),
                value: o
              })
            }))
        : [],
    [makeOptionKey, options]
  );

  const handleSelectBlur = useCallback(() => {
    if (!show) {
      setSelectFocused(false);
      if (onBlur) onBlur({ field: name, value });
    }
  }, [name, onBlur, show, value]);

  const handleSelectKeyPress = event => {
    const k = event.key;

    // if keydown includes CTRL or ALT key, then ignore
    if (event.metaKey) return false;

    if (k === "Tab" || options?.length === 0) {
      return;
    }
    // prevents page from scrolling down when opening the menu
    event.preventDefault();
    event.stopPropagation();

    if (onKeyDown) onKeyDown({ event, value });

    if (!show && k === "ArrowDown") {
      setShow(true);
      setTimeout(() => {
        const firstItem = menuRef?.current?.querySelector("button");
        if (firstItem) firstItem?.focus();
      });
    } else if (show && k === "ArrowDown") {
      const firstItem = menuRef?.current?.querySelector("button");
      if (firstItem) firstItem?.focus();
    } else if (show && k === "Escape") setShow(false);
    else if (k.length === 1) {
      setShow(true);
      setOptionFilter(k);
    }
  };

  const handleSelectKeyDown = event => {
    const k = event.key;
    if (k === "Tab") {
      setShow(false);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const focusMainElement = () => {
    setSelectFocused(true);
    const select = mainRef?.current?.querySelector("button.uikit__select__btn");
    if (select) select?.focus();
  };

  const handleClick = () => {
    focusMainElement();
    if (opts.length > 0) setShow(!show); // toggles options when user clicks on input multiple times
  };

  const isSelectObj = v => {
    if (isObject(v)) {
      if (JSON.stringify(v) === "{}") return true;
      else if (Object.keys(v).includes("value")) return true;
      return false;
    }
    return false;
  };

  const handleOptionSelect = option => {
    // is SINGLE SELECT
    if (!multi) {
      setShow(false);
      setSelectedOption(option);
      /**
       * if the initial value is a string, then update value with the 'option.value'
       * ... if it's not a string, check to see if the initial value is an object (empty or a valid option type)
       */

      const v =
        typeof value === "string"
          ? option?.value || ""
          : typeof value === "number"
          ? option?.value || 0
          : isSelectObj(value)
          ? option
          : "";

      onChange({
        field: name,
        value: v
      });
      focusMainElement();
    } else {
      const newValue = value?.includes(option?.value)
        ? value.filter(o => o !== option?.value)
        : [...(value || []), option?.value];

      onChange({
        field: name,
        value: newValue
      });
    }
  };

  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });
    return errors;
  };

  const handleClear = () => {
    setSelectedOption(null);
    onChange({
      field: name,
      value: !multi ? "" : []
    });
    focusMainElement();
  };

  // -------------------------
  //     FORM REGISTRATION
  // -------------------------

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label
  });

  useEffect(() => {
    if (!multi) {
      if (opts?.length && value !== selectedOption?.value) {
        setSelectedOption(opts.find(o => o.value === value));
      }
    } else if (opts?.length && !(selectedOption || []).includes(value)) {
      setSelectedOption(opts.find(o => o.value === value));
    }
  }, [opts, value, multi, selectedOption]);

  useEffect(() => {
    if (!show && !!optionFilter) setOptionFilter("");
  }, [optionFilter, show]);

  const wasShown = usePrevious(show);
  useEffect(() => {
    if (wasShown && !show) handleSelectBlur();
  }, [wasShown, show, handleSelectBlur]);

  const isDisabled = disabled || isLoading;

  const hasValue =
    (!multi &&
      ((isSelectObj(value) && value.value) ||
        (!isSelectObj(value) && value))) ||
    (multi && !!value?.length);

  const showClearButton =
    !isLoading && isClearable && !required && !isDisabled && hasValue;

  const getOpt = v =>
    (opts || []).find(
      o => o.value === v || (isObject(value) && o.value === v.value)
    ) || {};

  // -------------------------
  //     STYLES/CLAASES
  // -------------------------
  const sizeClass = size ? "uikit__select-size__" + size : null;

  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__select-wrapper",
    className,
    sizeClass,
    {
      "uikit__hide-label": hideLabel,
      "uikit__form__hide-errors": hideErrors,
      uikit__select__expanded: show,
      "uikit__select-disabled": isDisabled,
      "uikit__select-wrapper-multi": multi
    }
  );

  const selectClass = classnames("uikit__select__btn", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": hasValue,
    "uikit__select__show-clear-button": showClearButton
  });

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      <div
        className={classnames("uikit__select", {
          "uikit__select-multi": multi
        })}
      >
        <div
          id={`${id}__wrapper`}
          ref={mainRef}
          className="uikit__select__btn-wrapper"
        >
          <button
            aria-describedby={
              !hideErrors && !!fieldErrors?.length && `errors_for_${id}`
            }
            role="listbox"
            aria-expanded={show}
            autoComplete="off"
            data-1p-ignore
            type="button"
            id={id}
            name={name}
            className={selectClass}
            onBlur={handleSelectBlur}
            onFocus={focusMainElement}
            onClick={handleClick}
            onKeyUp={handleSelectKeyPress}
            onKeyDown={handleSelectKeyDown}
            disabled={isDisabled}
            {...createDataAttributes(dataAttrib)}
            {...rest}
          >
            {!isLoading && !hasValue && (
              <span className="uikit__select__placeholder">{placeholder}</span>
            )}
            {isLoading && (
              <span className="uikit__select__placeholder">
                {loadingMessage}
              </span>
            )}
            <span className="uikit__select__selected-label">
              {multi ? (
                <ul className="uikit__select-multi__labels">
                  {(value || []).map(v => (
                    <li role="list" key={getOpt(v)?.value}>
                      {getOpt(v)?.label}
                    </li>
                  ))}
                </ul>
              ) : (
                getOpt(value)?.label || ""
              )}
            </span>
            <span aria-hidden="true" className="uikit__select__selected-value">
              {multi
                ? (value || []).map(v => (
                    <React.Fragment key={getOpt(v)?.value}>
                      {getOpt(v)?.value},
                    </React.Fragment>
                  ))
                : getOpt(value)?.value || ""}
            </span>
          </button>
          {showClearButton && (
            <button
              type="button"
              className="uikit__select__clear"
              onClick={handleClear}
              disabled={isDisabled}
            >
              <Icon icon="farTimes" />
            </button>
          )}
          <button
            className="uikit__select__indicator"
            tabIndex="-1"
            disabled={isDisabled}
            type="button"
            onClick={handleClick}
          >
            <Icon icon={show ? "fasAngleDown" : "farAngleDown"} />
          </button>
          {
            /**only show option menu if options actually exist */
            options?.length > 0 && show && (
              <SelectMenu
                ref={menuRef}
                id={id}
                value={value}
                groupedOptions
                options={opts}
                mainRef={mainRef}
                setShow={setShow}
                onSelect={handleOptionSelect}
                optionFilter={optionFilter}
                setOptionFilter={setOptionFilter}
                multi={multi}
                showSearch={showSearch}
              />
            )
          }
        </div>

        {!selectFocused && !show && !hideErrors && !!fieldErrors?.length && (
          <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
        )}
      </div>
    </div>
  );
};

Select.propTypes = {
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.number,
    propTypes.array
  ]).isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func,
  required: propTypes.bool,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,
  requiredError: propTypes.string,
  placeholder: propTypes.string,
  options: propTypes.array.isRequired,
  isLoading: propTypes.bool,
  isClearable: propTypes.bool,
  showSearch: propTypes.bool,
  loadingMessage: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  onOptionSelect: propTypes.func,
  onReset: propTypes.func,
  multi: propTypes.bool,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  wrapperClassName: propTypes.string,
  componentname: propTypes.string
};

Select.defaultProps = {
  componentname: "Select",
  disabled: false,
  size: "md",
  isClearable: true,
  showSearch: false,
  placeholder: "Select One",
  loadingMessage: "Loading..."
};

export default Select;
